/**
 * Per-Room Identity Generation
 *
 * Creates ephemeral keypairs for each room to ensure unlinkability across rooms.
 */

import * as ed from '@noble/ed25519'
import { sha512 } from '@noble/hashes/sha512'

// Configure ed25519 to use sha512
ed.etc.sha512Sync = (...m) => sha512(ed.etc.concatBytes(...m))

/**
 * Generate a new Ed25519 keypair for room identity
 */
export async function generateIdentity(): Promise<{
  privateKey: Uint8Array
  publicKey: Uint8Array
  publicKeyBase64: string
}> {
  // Generate random private key
  const privateKey = ed.utils.randomPrivateKey()

  // Derive public key
  const publicKey = await ed.getPublicKeyAsync(privateKey)

  return {
    privateKey,
    publicKey,
    publicKeyBase64: uint8ArrayToBase64(publicKey),
  }
}

/**
 * Sign a message with the private key
 */
export async function signMessage(
  message: Uint8Array,
  privateKey: Uint8Array
): Promise<Uint8Array> {
  return await ed.signAsync(message, privateKey)
}

/**
 * Verify a signature
 */
export async function verifySignature(
  signature: Uint8Array,
  message: Uint8Array,
  publicKey: Uint8Array
): Promise<boolean> {
  return await ed.verifyAsync(signature, message, publicKey)
}

/**
 * Derive a peer ID from a public key
 * Uses first 20 bytes of SHA-256 hash
 */
export async function derivePeerId(publicKey: Uint8Array): Promise<string> {
  // Create a copy to ensure proper ArrayBuffer type
  const hash = await crypto.subtle.digest('SHA-256', new Uint8Array(publicKey).buffer)
  const bytes = new Uint8Array(hash).slice(0, 20)
  return uint8ArrayToHex(bytes)
}

// ============= Utility Functions =============

export function uint8ArrayToBase64(bytes: Uint8Array): string {
  const binary = String.fromCharCode(...bytes)
  return btoa(binary)
}

export function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

export function uint8ArrayToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export function hexToUint8Array(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2)
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16)
  }
  return bytes
}
